import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import {
  CustomerActivePaymentMethodsDocument,
  useFetchCustomerActivePaymentMethods,
  useMutateChooseDefaultPaymentMethod,
  useMutateRemovePaymentMethod,
} from '@entities/Payment'

import { useErrorInfoModal } from '@features/modal'



export function useManagePaymentMethods () {
  const { t: te } = useTranslation('errors')
  const { openErrorInfoModal } = useErrorInfoModal()
  const { data, loading } = useFetchCustomerActivePaymentMethods({
    fetchPolicy: 'cache-and-network',
  })
  const [ chooseDefault ] = useMutateChooseDefaultPaymentMethod()
  const [ removeMethod ] = useMutateRemovePaymentMethod()

  const paymentMethods = useMemo(() => data?.customerActivePaymentMethods ?? [], [ data?.customerActivePaymentMethods ])

  const defaultCard = paymentMethods.find((m) => m?.isDefault) ?? null

  const handleDefault = useCallback(
    async (id: string) => {
      const alreadyDefault = paymentMethods.some(
        (method) => method?.id === id && method?.isDefault,
      )

      if (alreadyDefault) return

      try {
        await chooseDefault({
          variables: { paymentMethodId: id },
          update (cache) {
            cache.writeQuery({
              query: CustomerActivePaymentMethodsDocument,
              data: {
                __typename: 'Query',
                customerActivePaymentMethods: paymentMethods.map((method) => ({
                  ...method,
                  isDefault: method?.id === id,
                  __typename: 'CustomerPaymentMethod',
                })),
              },
            })
          },
        })
      }
      catch (error) {
        console.error('Error choosing default payment method:', error)
        openErrorInfoModal({
          description: te('choose_default_payment_method'),
        })
      }
    },
    [ chooseDefault, paymentMethods, te, openErrorInfoModal ],
  )

  const handleRemove = useCallback(
    async (id: string) => {
      try {
        await removeMethod({
          variables: { removePaymentMethodId: id },
          update (cache, result) {
            const {
              id = null,
              removed = false
            } = result.data?.removePaymentMethod ?? {}

            if (id && removed) {
              cache.writeQuery({
                query: CustomerActivePaymentMethodsDocument,
                data: {
                  __typename: 'Query',
                  customerActivePaymentMethods: paymentMethods
                    .filter((method) => method?.id !== id)
                    .map((method) => ({
                      ...method,
                      __typename: 'CustomerPaymentMethod',
                    })),
                },
              })
            }
          },
        })
      }
      catch (error) {
        console.error('Error removing payment method:', error)
        openErrorInfoModal({
          description: te('remove_payment_method'),
        })
      }
    },
    [ removeMethod, paymentMethods, te, openErrorInfoModal ],
  )

  return {
    paymentMethods,
    defaultCard,
    loading,
    handleDefault,
    handleRemove,
  }
}
